import React, { useState, useCallback, useRef, useEffect } from 'react';
import ReactCrop, {
  type Crop,
  type PixelCrop,
  centerCrop,
  makeAspectCrop,
} from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import {
  XMarkIcon,
  ArrowPathIcon,
  ArrowsRightLeftIcon,
  ArrowsUpDownIcon,
  SunIcon,
  Squares2X2Icon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { Photo, getPhotoUrl } from '../../api/photos';

/** Fetch image as blob with auth so it loads in editor and can be drawn to canvas (no CORS taint). */
async function fetchImageAsBlobUrl(imageUrl: string): Promise<string> {
  const token = localStorage.getItem('token');
  const headers: Record<string, string> = {};
  if (token?.trim()) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(imageUrl, { credentials: 'include', headers });
  if (!res.ok) throw new Error(`Failed to load image: ${res.status}`);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number
): Crop {
  return centerCrop(
    makeAspectCrop(
      { unit: '%', width: 90 },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

export interface ImageEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  photo: Photo | null;
  onSave: (photoId: string, formData: FormData) => Promise<void>;
}

const ROTATION_OPTIONS = [0, 90, 180, 270] as const;
const MIN_SCALE = 25;
const MAX_SCALE = 200;
const MIN_BRIGHTNESS = 50;
const MAX_BRIGHTNESS = 150;
const MIN_CONTRAST = 50;
const MAX_CONTRAST = 150;

const ImageEditorModal: React.FC<ImageEditorModalProps> = ({
  isOpen,
  onClose,
  photo,
  onSave,
}) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [imageLoadError, setImageLoadError] = useState<string | null>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [rotation, setRotation] = useState(0);
  const [scalePercent, setScalePercent] = useState(100);
  const [flipH, setFlipH] = useState(false);
  const [flipV, setFlipV] = useState(false);
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [saving, setSaving] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Load image as blob URL when modal opens with a photo (works with auth + no CORS taint on canvas)
  // Use relative URL when possible so Vite dev proxy is used (avoids CORS when frontend and API on different ports)
  useEffect(() => {
    if (!isOpen || !photo) {
      setImageSrc(null);
      setImageLoadError(null);
      setImageLoaded(false);
      return;
    }
    const fullUrl = getPhotoUrl(photo.url ?? '');
    if (!fullUrl) {
      setImageLoadError('No image URL');
      setImageSrc(null);
      return;
    }
    // Always fetch the absolute API URL. Pathname-only requests hit the Pages
    // origin in production (no Vite proxy), so the image never loads.
    const fetchUrl = fullUrl;
    setImageLoadError(null);
    setImageLoaded(false);
    setImageSrc(null);
    let revoked = false;
    let blobUrlToRevoke: string | null = null;
    fetchImageAsBlobUrl(fetchUrl)
      .then((blobUrl) => {
        if (!revoked) {
          blobUrlToRevoke = blobUrl;
          setImageSrc(blobUrl);
        } else {
          URL.revokeObjectURL(blobUrl);
        }
      })
      .catch((err) => {
        if (!revoked) {
          setImageLoadError(err instanceof Error ? err.message : 'Failed to load image');
        }
      });
    return () => {
      revoked = true;
      if (blobUrlToRevoke) URL.revokeObjectURL(blobUrlToRevoke);
    };
  }, [isOpen, photo?.id, photo?.url]);

  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    const initialCrop = centerAspectCrop(width, height, 1);
    setCrop(initialCrop);
    setImageLoaded(true);
  }, []);

  const getCroppedCanvas = useCallback(
    (
      image: HTMLImageElement,
      crop: PixelCrop,
      rot: number,
      scale: number,
      flipHorizontal: boolean,
      flipVertical: boolean,
      bright: number,
      cont: number
    ): HTMLCanvasElement => {
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;

      const cropX = crop.x * scaleX;
      const cropY = crop.y * scaleY;
      const cropW = crop.width * scaleX;
      const cropH = crop.height * scaleY;

      const rotRad = (rot * Math.PI) / 180;
      const outW =
        rot === 90 || rot === 270
          ? Math.floor(cropH * (scale / 100))
          : Math.floor(cropW * (scale / 100));
      const outH =
        rot === 90 || rot === 270
          ? Math.floor(cropW * (scale / 100))
          : Math.floor(cropH * (scale / 100));

      const canvas = document.createElement('canvas');
      canvas.width = outW;
      canvas.height = outH;
      const ctx = canvas.getContext('2d');
      if (!ctx) return canvas;

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      const flipX = flipHorizontal ? -1 : 1;
      const flipY = flipVertical ? -1 : 1;
      ctx.filter = `brightness(${bright / 100}) contrast(${cont / 100})`;

      ctx.translate(outW / 2, outH / 2);
      ctx.scale(flipX, flipY);
      ctx.rotate(rotRad);
      ctx.scale(scale / 100, scale / 100);
      ctx.translate(-cropW / 2, -cropH / 2);
      ctx.drawImage(image, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);

      return canvas;
    },
    []
  );

  const handleSave = useCallback(async () => {
    if (!photo || !imgRef.current || !completedCrop) return;
    setSaving(true);
    try {
      const canvas = getCroppedCanvas(
        imgRef.current,
        completedCrop,
        rotation,
        scalePercent,
        flipH,
        flipV,
        brightness,
        contrast
      );
      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(resolve, 'image/jpeg', 0.92);
      });
      if (!blob) {
        throw new Error('Failed to create image');
      }
      const file = new File([blob], photo.filename || 'edited-photo.jpg', {
        type: 'image/jpeg',
      });
      const formData = new FormData();
      formData.append('file', file);
      await onSave(photo.id, formData);
      onClose();
    } catch (err) {
      console.error('Save image failed:', err);
    } finally {
      setSaving(false);
    }
  }, [
    photo,
    completedCrop,
    rotation,
    scalePercent,
    flipH,
    flipV,
    brightness,
    contrast,
    getCroppedCanvas,
    onSave,
    onClose,
  ]);

  const resetEdits = useCallback(() => {
    setRotation(0);
    setScalePercent(100);
    setFlipH(false);
    setFlipV(false);
    setBrightness(100);
    setContrast(100);
    if (imgRef.current && imageLoaded) {
      const { width, height } = imgRef.current;
      setCrop(centerAspectCrop(width, height, 1));
    }
  }, [imageLoaded]);

  if (!isOpen || !photo) return null;

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 bg-gray-900 bg-opacity-90 transition-opacity"
          onClick={onClose}
          aria-hidden="true"
        />
        <div className="inline-block align-bottom bg-gray-800 rounded-xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full">
          <div className="px-4 pt-5 pb-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Edit Image</h3>
              <button
                type="button"
                onClick={onClose}
                className="text-slate-400 hover:text-white"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="flex flex-col lg:flex-row gap-6">
              {/* Crop area */}
              <div className="flex-1 flex justify-center bg-black rounded-lg overflow-hidden min-h-[280px] max-h-[60vh]">
                {imageLoadError && (
                  <div className="flex flex-col items-center justify-center p-8 text-red-400">
                    <p className="text-sm">{imageLoadError}</p>
                    <p className="mt-2 text-xs text-slate-400">Check that the photo URL is reachable and you are logged in.</p>
                  </div>
                )}
                {!imageLoadError && !imageSrc && (
                  <div className="flex items-center justify-center p-8 text-slate-400">
                    <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-500 border-t-indigo-500" />
                    <span className="ml-3">Loading image…</span>
                  </div>
                )}
                {!imageLoadError && imageSrc && (
                  <div className="relative max-w-full max-h-[60vh] flex items-center justify-center w-full">
                    <ReactCrop
                      crop={crop}
                      onChange={(pixelCrop) => {
                        setCrop(pixelCrop);
                        setCompletedCrop(pixelCrop);
                      }}
                      onComplete={(pixelCrop) => setCompletedCrop(pixelCrop)}
                      aspect={undefined}
                      className="max-h-[60vh]"
                    >
                      <img
                        key={photo.id}
                        ref={imgRef}
                        src={imageSrc}
                        alt="Edit"
                        onLoad={onImageLoad}
                        onError={() => setImageLoadError('Image failed to load')}
                        style={{
                          maxHeight: '60vh',
                          width: 'auto',
                          display: 'block',
                          transform: `rotate(${rotation}deg) scaleX(${flipH ? -1 : 1}) scaleY(${flipV ? -1 : 1})`,
                          filter: `brightness(${brightness / 100}) contrast(${contrast / 100})`,
                        }}
                      />
                    </ReactCrop>
                  </div>
                )}
              </div>

              {/* Controls */}
              <div className="w-full lg:w-72 space-y-4 flex-shrink-0">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Rotation
                  </label>
                  <div className="flex gap-2 flex-wrap">
                    {ROTATION_OPTIONS.map((deg) => (
                      <button
                        key={deg}
                        type="button"
                        onClick={() => setRotation(deg)}
                        className={`px-3 py-1.5 rounded-md text-sm ${
                          rotation === deg
                            ? 'bg-cyan-600 text-white'
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        }`}
                      >
                        {deg}°
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Scale: {scalePercent}%
                  </label>
                  <input
                    type="range"
                    min={MIN_SCALE}
                    max={MAX_SCALE}
                    value={scalePercent}
                    onChange={(e) => setScalePercent(Number(e.target.value))}
                    className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer accent-indigo-500"
                  />
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setFlipH((v) => !v)}
                    className={`inline-flex items-center px-3 py-2 rounded-md text-sm ${
                      flipH ? 'bg-cyan-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                    title="Flip horizontal"
                  >
                    <ArrowsRightLeftIcon className="h-4 w-4 mr-1" />
                    Flip H
                  </button>
                  <button
                    type="button"
                    onClick={() => setFlipV((v) => !v)}
                    className={`inline-flex items-center px-3 py-2 rounded-md text-sm ${
                      flipV ? 'bg-cyan-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                    title="Flip vertical"
                  >
                    <ArrowsUpDownIcon className="h-4 w-4 mr-1" />
                    Flip V
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1 flex items-center">
                    <SunIcon className="h-4 w-4 mr-1" />
                    Brightness: {brightness}%
                  </label>
                  <input
                    type="range"
                    min={MIN_BRIGHTNESS}
                    max={MAX_BRIGHTNESS}
                    value={brightness}
                    onChange={(e) => setBrightness(Number(e.target.value))}
                    className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer accent-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1 flex items-center">
                    <Squares2X2Icon className="h-4 w-4 mr-1" />
                    Contrast: {contrast}%
                  </label>
                  <input
                    type="range"
                    min={MIN_CONTRAST}
                    max={MAX_CONTRAST}
                    value={contrast}
                    onChange={(e) => setContrast(Number(e.target.value))}
                    className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer accent-indigo-500"
                  />
                </div>

                <div className="flex gap-2 pt-2">
                  <button
                    type="button"
                    onClick={resetEdits}
                    className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-gray-300 bg-gray-700 hover:bg-gray-600"
                  >
                    <ArrowPathIcon className="h-4 w-4 mr-2" />
                    Reset
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving || !completedCrop || !!imageLoadError || !imageSrc}
                    className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-white bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <CheckIcon className="h-4 w-4 mr-2" />
                    {saving ? 'Saving...' : 'Save image'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageEditorModal;
